// UltraFlex Admin — EPOS Back Office
// Full visibility into EPOS operations: sessions, transactions, audit, journals, exceptions, gift cards, loyalty
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Monitor, FileText, Shield, BookOpen, AlertTriangle, Gift, Star,
  ChevronLeft, ChevronRight, Download, Eye, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminEposBackOffice() {
  const { token } = useAuth();
  const [tab, setTab] = useState('sessions');
  const [data, setData] = useState({ items: [], total: 0 });
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchSites(); }, []);
  useEffect(() => { fetchData(); }, [tab, selectedSite, page]);

  const fetchSites = async () => {
    const r = await fetch(`${API}/api/sites`, { headers });
    setSites(((await r.json()).items || []).filter(s => s.site_type !== 'warehouse'));
  };

  const fetchData = async () => {
    setLoading(true);
    setDetail(null);
    const siteParam = selectedSite ? `&site_id=${selectedSite}` : '';
    let url = '';
    switch (tab) {
      case 'sessions': url = `${API}/api/epos/sessions?page=${page}${siteParam}`; break;
      case 'transactions': url = `${API}/api/epos/transactions?site_id=${selectedSite || sites[0]?.id || ''}&page=${page}`; break;
      case 'audit': url = `${API}/api/epos/audit-log?page=${page}${siteParam}`; break;
      case 'journals': url = `${API}/api/epos/journal-entries?page=${page}${siteParam}`; break;
      case 'exceptions': url = `${API}/api/epos/exceptions?${siteParam.replace('&', '')}`; break;
      case 'gift_cards': url = `${API}/api/gift-cards?page=${page}`; break;
      case 'loyalty': url = `${API}/api/loyalty/campaigns`; break;
      case 'tills': url = `${API}/api/epos/tills?site_id=${selectedSite || sites[0]?.id || ''}`; break;
      case 'staff_perf': url = `${API}/api/epos/reports/staff-performance?site_id=${selectedSite || sites[0]?.id || ''}`; break;
      case 'refund_void': url = `${API}/api/epos/reports/refund-void?site_id=${selectedSite || sites[0]?.id || ''}`; break;
      default: url = `${API}/api/epos/sessions?page=${page}`;
    }
    try {
      const r = await fetch(url, { headers });
      const d = await r.json();
      setData(d.items ? d : { items: d, total: (d.items || d).length });
    } catch (e) { console.error(e); setData({ items: [], total: 0 }); }
    setLoading(false);
  };

  const viewZReport = async (sessionId) => {
    const r = await fetch(`${API}/api/epos/z-report/${sessionId}`, { headers });
    if (r.ok) setDetail(await r.json());
  };

  const viewTransaction = async (txnId) => {
    const r = await fetch(`${API}/api/epos/transactions/${txnId}`, { headers });
    if (r.ok) setDetail(await r.json());
  };

  const tabs = [
    { key: 'sessions', label: 'Till Sessions', icon: Monitor },
    { key: 'transactions', label: 'Transactions', icon: FileText },
    { key: 'audit', label: 'Audit Log', icon: Shield },
    { key: 'journals', label: 'Journals', icon: BookOpen },
    { key: 'exceptions', label: 'Exceptions', icon: AlertTriangle },
    { key: 'staff_perf', label: 'Staff Performance', icon: Monitor },
    { key: 'refund_void', label: 'Refunds & Voids', icon: AlertTriangle },
    { key: 'gift_cards', label: 'Gift Cards', icon: Gift },
    { key: 'loyalty', label: 'Loyalty', icon: Star },
    { key: 'tills', label: 'Tills', icon: Monitor },
  ];

  const items = Array.isArray(data) ? data : (data.items || []);

  return (
    <div className="space-y-6" data-testid="epos-backoffice">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">EPOS Back Office</h1>
          <p className="text-sm text-gray-400 mt-1">Full visibility into EPOS operations, audit trail, and financials</p>
        </div>
        <select value={selectedSite} onChange={e => { setSelectedSite(e.target.value); setPage(1); }} className="bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm" data-testid="bo-site-filter">
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-gray-800 pb-2">
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setPage(1); setDetail(null); }} data-testid={`bo-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-t text-sm whitespace-nowrap ${tab === t.key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      <div className="flex gap-4">
        {/* Main table */}
        <div className={`flex-1 bg-[#111] border border-gray-800 rounded-xl overflow-hidden ${detail ? 'max-w-[60%]' : ''}`}>
          {loading ? <div className="text-gray-500 text-center py-8">Loading...</div> : (
            <div className="overflow-x-auto">
              {tab === 'sessions' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">Site</th><th className="p-3">Till</th><th className="p-3">Staff</th>
                  <th className="p-3 text-right">Net Sales</th><th className="p-3">Txns</th><th className="p-3">Variance</th>
                  <th className="p-3">Status</th><th className="p-3">Opened</th><th className="p-3"></th>
                </tr></thead><tbody>
                  {items.map(s => (
                    <tr key={s.id} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                      <td className="p-3 text-white text-xs">{s.site_name}</td>
                      <td className="p-3 text-gray-300 text-center">{s.till_number}</td>
                      <td className="p-3 text-gray-300 text-xs">{s.staff_name}</td>
                      <td className="p-3 text-right text-green-400 font-medium">£{s.net_sales?.toFixed(2)}</td>
                      <td className="p-3 text-center text-gray-300">{s.transaction_count}</td>
                      <td className={`p-3 text-center ${Math.abs(s.cash_variance) > 5 ? 'text-red-400 font-bold' : 'text-gray-500'}`}>£{s.cash_variance?.toFixed(2)}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${s.status === 'open' ? 'bg-green-900/40 text-green-400' : 'bg-gray-800 text-gray-400'}`}>{s.status}</span>{s.is_training && <span className="text-xs ml-1 text-yellow-400">(T)</span>}</td>
                      <td className="p-3 text-gray-500 text-xs">{s.opened_at?.split('T')[0]}</td>
                      <td className="p-3">{s.status === 'closed' && <button onClick={() => viewZReport(s.id)} className="text-blue-400 text-xs hover:underline">Z-Report</button>}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {tab === 'transactions' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">TXN #</th><th className="p-3">Type</th><th className="p-3">Member</th>
                  <th className="p-3 text-right">Total</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="p-3"></th>
                </tr></thead><tbody>
                  {items.map(t => (
                    <tr key={t.id} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                      <td className="p-3 text-white font-mono text-xs">{t.txn_number}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${t.txn_type === 'refund' ? 'bg-orange-900/40 text-orange-400' : t.txn_type === 'void' ? 'bg-red-900/40 text-red-400' : 'bg-blue-900/40 text-blue-400'}`}>{t.txn_type}</span></td>
                      <td className="p-3 text-gray-400 text-xs">{t.member_name || 'Guest'}</td>
                      <td className={`p-3 text-right font-medium ${t.status === 'voided' ? 'text-red-400 line-through' : 'text-white'}`}>£{t.total?.toFixed(2)}</td>
                      <td className="p-3 text-gray-400 text-xs">{t.status}{t.is_training && ' (T)'}</td>
                      <td className="p-3 text-gray-500 text-xs">{t.created_at?.split('T')[0]}</td>
                      <td className="p-3"><button onClick={() => viewTransaction(t.id)} className="text-blue-400 text-xs hover:underline">View</button></td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {tab === 'audit' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">Action</th><th className="p-3">Description</th><th className="p-3">Time</th>
                </tr></thead><tbody>
                  {items.map(a => (
                    <tr key={a.id} className="border-b border-gray-800/50">
                      <td className="p-3"><span className="text-xs bg-gray-800 px-2 py-0.5 rounded text-gray-300">{a.action}</span></td>
                      <td className="p-3 text-gray-300 text-xs">{a.description}</td>
                      <td className="p-3 text-gray-500 text-xs whitespace-nowrap">{a.created_at?.replace('T', ' ').split('.')[0]}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {tab === 'journals' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">Reference</th><th className="p-3">Type</th><th className="p-3">Description</th>
                  <th className="p-3 text-right">Debit</th><th className="p-3 text-right">Credit</th><th className="p-3">A/C</th><th className="p-3">Date</th>
                </tr></thead><tbody>
                  {items.map(j => (
                    <tr key={j.id} className="border-b border-gray-800/50">
                      <td className="p-3 text-white font-mono text-xs">{j.reference}</td>
                      <td className="p-3 text-gray-400 text-xs">{j.journal_type}</td>
                      <td className="p-3 text-gray-300 text-xs">{j.description}</td>
                      <td className="p-3 text-right text-red-400">{j.debit_amount > 0 ? `£${j.debit_amount.toFixed(2)}` : ''}</td>
                      <td className="p-3 text-right text-green-400">{j.credit_amount > 0 ? `£${j.credit_amount.toFixed(2)}` : ''}</td>
                      <td className="p-3 text-gray-500 text-xs">{j.account_code}</td>
                      <td className="p-3 text-gray-500 text-xs">{j.period_date}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {tab === 'exceptions' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">Type</th><th className="p-3">Description</th><th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-right">Threshold</th><th className="p-3">Flagged</th><th className="p-3">Resolved</th>
                </tr></thead><tbody>
                  {items.map(e => (
                    <tr key={e.id} className="border-b border-gray-800/50">
                      <td className="p-3"><span className="text-xs bg-red-900/40 text-red-400 px-2 py-0.5 rounded">{e.type}</span></td>
                      <td className="p-3 text-gray-300 text-xs">{e.description}</td>
                      <td className="p-3 text-right text-red-400">£{e.amount?.toFixed(2)}</td>
                      <td className="p-3 text-right text-gray-500">£{e.threshold?.toFixed(2)}</td>
                      <td className="p-3 text-gray-500 text-xs">{e.flagged_at?.split('T')[0]}</td>
                      <td className="p-3">{e.resolved ? <span className="text-green-400 text-xs">Yes</span> : <span className="text-yellow-400 text-xs">Pending</span>}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {tab === 'gift_cards' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">Card #</th><th className="p-3 text-right">Balance</th><th className="p-3 text-right">Original</th>
                  <th className="p-3">Status</th><th className="p-3">Recipient</th><th className="p-3">Expires</th>
                </tr></thead><tbody>
                  {items.map(gc => (
                    <tr key={gc.id} className="border-b border-gray-800/50">
                      <td className="p-3 text-white font-mono text-xs">{gc.card_number}</td>
                      <td className="p-3 text-right text-green-400 font-medium">£{gc.balance?.toFixed(2)}</td>
                      <td className="p-3 text-right text-gray-300">£{gc.original_amount?.toFixed(2)}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${gc.status === 'active' ? 'bg-green-900/40 text-green-400' : gc.status === 'depleted' ? 'bg-gray-800 text-gray-400' : 'bg-red-900/40 text-red-400'}`}>{gc.status}</span></td>
                      <td className="p-3 text-gray-400 text-xs">{gc.recipient_name || '-'}</td>
                      <td className="p-3 text-gray-500 text-xs">{gc.expires_at?.split('T')[0]}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {tab === 'loyalty' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">Campaign</th><th className="p-3">Multiplier</th><th className="p-3">Categories</th>
                  <th className="p-3">Start</th><th className="p-3">End</th><th className="p-3">Active</th>
                </tr></thead><tbody>
                  {items.map(c => (
                    <tr key={c.id} className="border-b border-gray-800/50">
                      <td className="p-3 text-white">{c.name}</td>
                      <td className="p-3 text-yellow-400 font-bold text-center">{c.multiplier}x</td>
                      <td className="p-3 text-gray-400 text-xs">{c.qualifying_categories ? c.qualifying_categories.join(', ') : 'All'}</td>
                      <td className="p-3 text-gray-500 text-xs">{c.start_date?.split('T')[0]}</td>
                      <td className="p-3 text-gray-500 text-xs">{c.end_date?.split('T')[0]}</td>
                      <td className="p-3">{c.is_active ? <span className="text-green-400 text-xs">Active</span> : <span className="text-gray-500 text-xs">Inactive</span>}</td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-gray-600">No loyalty campaigns configured</td></tr>}
                </tbody></table>
              )}

              {tab === 'tills' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="p-3">Till #</th><th className="text-left p-3">Name</th><th className="p-3">Location</th><th className="p-3">Active</th>
                </tr></thead><tbody>
                  {items.map(t => (
                    <tr key={t.id} className="border-b border-gray-800/50">
                      <td className="p-3 text-white font-bold text-center">{t.till_number}</td>
                      <td className="p-3 text-gray-300">{t.name}</td>
                      <td className="p-3 text-gray-500 text-xs">{t.location_description || '-'}</td>
                      <td className="p-3 text-center">{t.is_active ? <span className="text-green-400 text-xs">Active</span> : <span className="text-red-400 text-xs">Inactive</span>}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {tab === 'staff_perf' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">Employee</th><th className="p-3 text-right">Txns</th><th className="p-3 text-right">Sales</th>
                  <th className="p-3 text-right">Avg Txn</th><th className="p-3 text-right">Refunds</th><th className="p-3 text-right">Voids</th><th className="p-3 text-right">Discounts</th>
                </tr></thead><tbody>
                  {items.map(s => (
                    <tr key={s.staff_id} className="border-b border-gray-800/50">
                      <td className="p-3 text-white text-xs">{s.staff_name} <span className="text-gray-600">{s.employee_number}</span></td>
                      <td className="p-3 text-right text-gray-300">{s.transaction_count}</td>
                      <td className="p-3 text-right text-green-400 font-medium">£{s.total_sales?.toFixed(2)}</td>
                      <td className="p-3 text-right text-gray-300">£{s.avg_transaction?.toFixed(2)}</td>
                      <td className="p-3 text-right text-orange-400">{s.refund_count} (£{s.refund_value?.toFixed(2)})</td>
                      <td className="p-3 text-right text-red-400">{s.void_count}</td>
                      <td className="p-3 text-right text-yellow-400">£{s.discount_given?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {tab === 'refund_void' && (
                <table className="w-full text-sm"><thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">TXN #</th><th className="p-3">Type</th><th className="p-3 text-right">Amount</th>
                  <th className="p-3">Staff</th><th className="p-3">Authorised By</th><th className="p-3">Reason</th><th className="p-3">Date</th>
                </tr></thead><tbody>
                  {items.map((r, i) => (
                    <tr key={i} className="border-b border-gray-800/50">
                      <td className="p-3 text-white font-mono text-xs">{r.txn_number}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${r.type === 'void' ? 'bg-red-900/40 text-red-400' : 'bg-orange-900/40 text-orange-400'}`}>{r.type}</span></td>
                      <td className="p-3 text-right text-white font-medium">£{r.amount?.toFixed(2)}</td>
                      <td className="p-3 text-gray-300 text-xs">{r.staff_name}</td>
                      <td className="p-3 text-gray-400 text-xs">{r.authorised_by || '-'}</td>
                      <td className="p-3 text-gray-500 text-xs">{r.reason || '-'}</td>
                      <td className="p-3 text-gray-500 text-xs">{r.created_at?.split('T')[0]}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}

              {items.length === 0 && tab !== 'loyalty' && <div className="text-center py-8 text-gray-600">No data for this view</div>}
            </div>
          )}

          {/* Pagination */}
          {data.total > 20 && (
            <div className="flex items-center justify-between p-3 border-t border-gray-800">
              <span className="text-gray-500 text-xs">{data.total} records</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-gray-400 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-white text-sm">Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} className="text-gray-400"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {detail && (
          <div className="w-[380px] bg-[#111] border border-gray-800 rounded-xl p-4 overflow-y-auto max-h-[calc(100vh-200px)]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-bold text-sm">{detail.report_type || detail.txn_number || 'Detail'}</h3>
              <button onClick={() => setDetail(null)} className="text-gray-500 text-xs">Close</button>
            </div>

            {/* Z-Report detail */}
            {detail.report_type && <>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-gray-900 rounded p-2 text-center"><div className="text-lg font-bold text-white">£{detail.totals?.net_sales?.toFixed(2)}</div><div className="text-xs text-gray-500">Net Sales</div></div>
                <div className="bg-gray-900 rounded p-2 text-center"><div className="text-lg font-bold text-white">{detail.counts?.transactions}</div><div className="text-xs text-gray-500">Txns</div></div>
              </div>
              {detail.cash_management && <div className="space-y-1 text-xs mb-3">
                <div className="flex justify-between"><span className="text-gray-500">Float:</span><span className="text-white">£{detail.cash_management.opening_float?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Cash Sales:</span><span className="text-white">£{detail.cash_management.cash_sales?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Drops:</span><span className="text-white">-£{detail.cash_management.cash_drops?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Expected:</span><span className="text-white font-bold">£{detail.cash_management.expected_in_drawer?.toFixed(2)}</span></div>
                {detail.cash_management.variance != null && <div className="flex justify-between"><span className="text-gray-500">Variance:</span><span className={Math.abs(detail.cash_management.variance) > 5 ? 'text-red-400 font-bold' : 'text-green-400'}>£{detail.cash_management.variance?.toFixed(2)}</span></div>}
              </div>}
              <div className="grid grid-cols-4 gap-1 text-center text-xs mb-3">
                <div><span className="text-white font-bold">{detail.counts?.refunds}</span><div className="text-gray-500">Refunds</div></div>
                <div><span className="text-white font-bold">{detail.counts?.voids}</span><div className="text-gray-500">Voids</div></div>
                <div><span className="text-white font-bold">{detail.counts?.no_sales}</span><div className="text-gray-500">No Sales</div></div>
                <div><span className="text-white font-bold">£{detail.totals?.refunds?.toFixed(2)}</span><div className="text-gray-500">Ref Val</div></div>
              </div>
              {detail.top_products?.length > 0 && <div className="border-t border-gray-800 pt-2"><div className="text-xs text-gray-500 uppercase mb-1">Top Products</div>{detail.top_products.slice(0, 5).map((p, i) => <div key={i} className="flex justify-between text-xs py-0.5"><span className="text-gray-300">{p.name}</span><span className="text-white">x{p.qty} £{p.total?.toFixed(2)}</span></div>)}</div>}
            </>}

            {/* Transaction detail */}
            {detail.txn_number && <>
              <div className="font-mono text-xs text-gray-300 bg-gray-950 rounded-lg p-3 space-y-1">
                <div className="text-center text-white font-bold mb-2">{detail.txn_number}</div>
                <div className="text-center text-gray-500 text-xs mb-2">{detail.created_at?.replace('T', ' ').split('.')[0]}</div>
                {detail.member_name && <div className="text-gray-400">Member: {detail.member_name}</div>}
                <div className="border-t border-dashed border-gray-700 my-1" />
                {detail.items?.map((i, idx) => <div key={idx} className="flex justify-between"><span>{i.product_name} x{i.quantity}</span><span>£{i.line_total?.toFixed(2)}</span></div>)}
                {detail.discount_total > 0 && <div className="flex justify-between text-green-400"><span>Discount</span><span>-£{detail.discount_total?.toFixed(2)}</span></div>}
                <div className="border-t border-dashed border-gray-700 my-1" />
                <div className="flex justify-between text-white font-bold"><span>TOTAL</span><span>£{detail.total?.toFixed(2)}</span></div>
                <div className="border-t border-dashed border-gray-700 my-1" />
                {detail.payments?.map((p, i) => <div key={i} className="flex justify-between capitalize"><span>{p.method}</span><span>£{p.amount?.toFixed(2)}</span></div>)}
              </div>
            </>}
          </div>
        )}
      </div>
    </div>
  );
}
